import { ref } from "vue";
import { documentationApi } from "@/api/documentation";

/**
 * Catalogue des guides visibles par le compte connecté.
 *
 * Le catalogue est chargé une seule fois par session : la navbar (bouton
 * d'aide) et l'écran Documentation partagent la même liste, sans se doubler
 * la requête. Le filtrage par droits est fait côté serveur — ce qui arrive
 * ici est déjà ce que l'utilisateur a le droit de lire.
 *
 * Une tentative qui échoue n'est JAMAIS rejouée automatiquement. `PageHeader`
 * appelle `load()` au montage, sur tous les écrans du back-office et parfois
 * plusieurs fois par écran : sans ce garde, un premier échec relançait un appel
 * à chaque montage suivant, la limitation de débit se déclenchait et
 * s'entretenait toute seule (« Too Many Attempts » en rafale).
 */
const guides = ref([]);
const loaded = ref(false);
const erreur = ref(false);
let pending = null;

export function useDocumentationCatalog() {
    /** Charge le catalogue (une fois), puis le renvoie. */
    const load = async () => {
        if (loaded.value || erreur.value) return guides.value;
        if (!pending) {
            pending = documentationApi
                .index()
                .then(reponse => {
                    // `request` renvoie déjà le corps de la réponse (son
                    // intercepteur retourne `response.data`) : ce qui arrive ici
                    // est donc `{ data: [...] }`, pas l'objet axios.
                    guides.value = reponse?.data ?? [];
                    loaded.value = true;
                    return guides.value;
                })
                .catch(() => {
                    // On retient l'échec plutôt que de réessayer : l'aide est un
                    // confort, elle ne doit pas marteler l'API ni masquer la
                    // vraie cause derrière une limitation de débit.
                    erreur.value = true;
                    return guides.value;
                })
                .finally(() => {
                    pending = null;
                });
        }
        return pending;
    };

    /** Guide associé à une route Vue, s'il en existe un de visible. */
    const guideForRoute = routeName =>
        guides.value.find(guide => guide.routes.includes(routeName)) ?? null;

    /** Vide le cache — à appeler à la déconnexion, les droits changent. */
    const reset = () => {
        guides.value = [];
        loaded.value = false;
        erreur.value = false;
        pending = null;
    };

    /** Nouvelle tentative explicite, sur action de l'utilisateur seulement. */
    const retry = () => {
        erreur.value = false;
        return load();
    };

    return { guides, loaded, erreur, load, guideForRoute, reset, retry };
}
